/**
 * Client-side background soft-removal for head crops.
 * Conservative: only removes pixels that clearly match sampled corner background
 * or are near-black — avoids eating dark skin / mid-tones (previous hard binary pass).
 */

/**
 * Gently pull visible subject pixels toward opaque (never reduces alpha).
 */
function liftSubjectAlpha(imageData) {
  const d = imageData.data;
  for (let i = 0; i < d.length; i += 4) {
    const a = d[i + 3];
    if (a < 8) continue;
    const lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
    if (lum < 18) continue;
    const lift = lum > 75 ? 0.5 : lum > 40 ? 0.38 : 0.22;
    d[i + 3] = Math.min(255, Math.round(a + (255 - a) * lift));
  }
}

/**
 * Mutates canvas: soft alpha from corner color distance + near-black crush only.
 * No hard 0/255 binarization (that was stripping skin).
 */
export function keyOutHeadBackground(canvas, options = {}) {
  const innerDist = options.innerDist ?? 38;
  const outerDist = options.outerDist ?? 58;

  const w = canvas.width;
  const h = canvas.height;
  const ctx = canvas.getContext('2d');
  if (!ctx || w < 4 || h < 4) return;

  const imageData = ctx.getImageData(0, 0, w, h);
  const d = imageData.data;

  const inset = Math.max(2, Math.floor(Math.min(w, h) * 0.02));
  const corners = [
    [inset, inset],
    [w - 1 - inset, inset],
    [inset, h - 1 - inset],
    [w - 1 - inset, h - 1 - inset],
  ];
  let cr = 0;
  let cg = 0;
  let cb = 0;
  let nSample = 0;
  for (const [x, y] of corners) {
    const idx = (y * w + x) * 4;
    if (d[idx + 3] < 20) continue;
    cr += d[idx];
    cg += d[idx + 1];
    cb += d[idx + 2];
    nSample += 1;
  }
  if (nSample === 0) return;
  cr /= nSample;
  cg /= nSample;
  cb /= nSample;

  const band = Math.max(6, outerDist - innerDist);

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const r = d[i];
      const g = d[i + 1];
      const b = d[i + 2];
      const dist = Math.hypot(r - cr, g - cg, b - cb);
      const lum = 0.299 * r + 0.587 * g + 0.114 * b;
      const a = d[i + 3];

      // Only true black — not dark skin
      if (lum < 10) {
        d[i + 3] = 0;
        continue;
      }

      if (dist < innerDist) {
        d[i + 3] = 0;
      } else if (dist < outerDist) {
        const t = (dist - innerDist) / band;
        d[i + 3] = Math.round(a * Math.max(0, Math.min(1, t)));
      } else {
        d[i + 3] = a;
      }

      // Do not let medium-bright pixels (likely skin) go fully transparent in the soft band
      if (lum > 48 && d[i + 3] < 40) {
        d[i + 3] = Math.max(d[i + 3], Math.round(90 + (lum - 48) * 2.2));
      }
    }
  }

  liftSubjectAlpha(imageData);
  ctx.putImageData(imageData, 0, 0);
}
